import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const CreateCustomer = ({ onCustomerCreated, onCancel }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [vpsList, setVpsList] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    isActive: true,
    phone: '',
    apiUrl: '',
    port: '',
    storeFrontendUrl: '',
    mongodbUri: '', mongodbDbName: '',
    jwtSecret: '', jwtExpire: '',
    r2AccessKeyId: '', r2SecretAccessKey: '', r2BucketName: '',
    r2PublicUrl: '', r2Endpoint: '', r2PublicBaseUrl: '',
    qdrantCollectionName: '',
    telegramBotToken: '', telegramBotUsername: '', telegramNotificationToken: '',
    aiImageCallbackUrl: '',
    messagingAppWebhookUrl: '',
    openaiApiKey: '', openaiModel: '', aiAgentMaxIterations: 50,
    adminDefaultEmail: '', adminDefaultPassword: '', adminDefaultName: '', adminDefaultPhone: '',
    vps: '',
    openaiProjectId: '',
    affiliate: '',
    agentPort: '',
    agentApiUrl: '',
    agentMongodbUri: '',
    agentMongodbDbName: '',
    webhookAppSecret: '',
    webhookReplayWindowSeconds: 300,
    ecommerceApiUrl: '',
    storeNameAr: '',
    storeAddress: '',
    storeFacebook: '',
    storeInstagram: '',
    storeType: '',
    aiAgentFrontendUrl: '',
    sessionEmailDomain: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [creatingR2, setCreatingR2] = useState(false);
  const [affiliatesList, setAffiliatesList] = useState([]);

  const extractDomainInfo = (apiUrl) => {
    try {
      const url = new URL(apiUrl);
      let host = url.hostname || '';
      if (host.startsWith('api.')) host = host.slice(4);
      const parts = host.split('.');
      const domainPart = parts.length > 0 ? parts[0] : host;
      return { host, domainPart };
    } catch (e) {
      return { host: '', domainPart: '' };
    }
  };

  const buildAiCallbackFromApiUrl = (apiUrl) => {
    try {
      const u = new URL(apiUrl);
      u.pathname = '/api/ai-image/callback';
      u.search = '';
      return u.toString();
    } catch {
      return '';
    }
  };

  const generateRandomHex = (len = 32) => {
    try {
      const bytes = new Uint8Array(len);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
  };

  const autoFillBackendFromApi = () => {
    const { apiUrl } = formData;
    try {
      const { host, domainPart } = extractDomainInfo(apiUrl);
      if (!host || !domainPart) {
        setError('صيغة API Public URL غير صحيحة. مثال: https://api.domain.com');
        return;
      }
      setFormData(prev => ({
        ...prev,
        storeFrontendUrl: `https://${host}`,
        mongodbUri: `mongodb://localhost:27017/${domainPart}`,
        mongodbDbName: domainPart,
        aiImageCallbackUrl: buildAiCallbackFromApiUrl(apiUrl) || prev.aiImageCallbackUrl || '',
        jwtExpire: prev.jwtExpire || '7d',
        adminDefaultEmail: `admin@${host}`,
        adminDefaultName: prev.adminDefaultName || 'مدير النظام',
        adminDefaultPhone: prev.adminDefaultPhone || prev.phone || '',
        openaiModel: prev.openaiModel || 'gpt-4.1-nano-2025-04-14',
        qdrantCollectionName: prev.qdrantCollectionName || `${domainPart}_products`,
        aiAgentMaxIterations: prev.aiAgentMaxIterations || 50,
      }));
      setSuccess('تم تعبئة الحقول تلقائياً بناءً على API Public URL');
    } catch {
      setError('حدث خطأ أثناء معالجة API Public URL');
    }
  };

  const handleGenerateJwtSecret = () => {
    setFormData(prev => ({
      ...prev,
      jwtSecret: generateRandomHex(32),
    }));
  };

  const handleGenerateAdminPassword = () => {
    setFormData(prev => ({
      ...prev,
      adminDefaultPassword: generateRandomHex(16),
    }));
  };

  const handleGenerateTelegramNotificationToken = () => {
    setFormData(prev => ({
      ...prev,
      telegramNotificationToken: generateRandomHex(24),
    }));
  };

  const handleGenerateWebhookAppSecret = () => {
    setFormData(prev => ({
      ...prev,
      webhookAppSecret: generateRandomHex(32),
    }));
  };

  useEffect(() => {
    fetchVPSs();
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const data = await apiRequest('/admin/affiliates?limit=500');
      if (data && data.success) {
        setAffiliatesList(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching affiliates:', err);
    }
  };

  const fetchVPSs = async () => {
    try {
      const data = await apiRequest('/admin/vps?limit=100');
      if (data && data.success) {
        setVpsList(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching VPSs:', error);
    }
  };

  const handleCreateR2 = async () => {
    try {
      const suggested = (formData.name || 'tijaraplus').toLowerCase().replace(/[^a-z0-9-]/g, '-');
      const bucketName = window.prompt('أدخل اسم البكت (اختياري):', `${suggested}-${Date.now()}`);
      if (bucketName === null) return;
      setCreatingR2(true);
      setError('');
      setSuccess('');
      const resp = await apiRequest('/admin/cloudflare/r2/create-bucket-and-token', {
        method: 'POST',
        body: JSON.stringify({ bucketName }),
      });
      if (resp && resp.success) {
        const { bucketName: bName, accessKeyId, secretAccessKey, endpoint, r2PublicUrl, r2PublicBaseUrl } = resp.data;
        setFormData(prev => ({
          ...prev,
          r2BucketName: bName,
          r2AccessKeyId: accessKeyId,
          r2SecretAccessKey: secretAccessKey,
          r2Endpoint: endpoint,
          r2PublicUrl: r2PublicUrl || prev.r2PublicUrl,
          r2PublicBaseUrl: r2PublicBaseUrl || prev.r2PublicBaseUrl,
        }));
        setSuccess('تم إنشاء R2 Bucket والتوكن وملء الحقول تلقائياً');
      } else {
        setError(resp?.message || 'فشل إنشاء R2 Bucket والتوكن');
      }
    } catch (err) {
      setError('حدث خطأ أثناء الاتصال بـ Cloudflare');
    } finally {
      setCreatingR2(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Clean up empty strings to avoid sending them (allow affiliate to be sent as '' for none)
      const cleanedData = Object.keys(formData).reduce((acc, key) => {
        if (key === 'affiliate') {
          acc[key] = formData[key] || null;
        } else if (formData[key] !== '') {
          acc[key] = formData[key];
        }
        return acc;
      }, {});

      const data = await apiRequest('/admin/customers', {
        method: 'POST',
        body: JSON.stringify(cleanedData),
      });

      if (data.success) {
        setSuccess('تم إنشاء العميل بنجاح');
        setApiToken(data.data.apiToken);
        setTimeout(() => {
          onCustomerCreated();
        }, 2000);
      } else {
        setError(data.message || data.errors?.join(', ') || 'حدث خطأ أثناء إنشاء العميل');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'البيانات الأساسية' },
    { id: 'backend', label: 'إعدادات الخادم' },
    { id: 'database', label: 'قاعدة البيانات' },
    { id: 'r2', label: 'R2 / Cloudflare' },
    { id: 'telegram', label: 'Telegram' },
    { id: 'admin', label: 'الإدارة' },
    { id: 'agent', label: 'وكيل الذكاء الاصطناعي' },
    { id: 'vps', label: 'VPS' },
    { id: 'ai', label: 'الذكاء الاصطناعي' },
  ];

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">إضافة عميل جديد</h2>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div>
            <div className="form-group">
              <label className="form-label">الاسم *</label>
              <input
                type="text"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">البريد الإلكتروني *</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">الهاتف</label>
              <input
                type="text"
                name="phone"
                className="form-control"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+9665XXXXXXXX"
              />
            </div>

            <div className="form-group">
              <label className="form-label">الشريك (مسوق بالعمولة)</label>
              <select
                name="affiliate"
                className="form-control"
                value={formData.affiliate || ''}
                onChange={handleChange}
              >
                <option value="">— لا يوجد —</option>
                {affiliatesList.filter(a => a.isActive !== false).map(a => (
                  <option key={a._id} value={a._id}>{a.name}{a.code ? ` (${a.code})` : ''}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  style={{ marginLeft: '0.5rem' }}
                />
                العميل نشط
              </label>
            </div>
          </div>
        )}

        {/* Backend Settings Tab */}
        {activeTab === 'backend' && (
          <div>
            <div className="form-group">
              <label className="form-label">API Public URL</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="url"
                  name="apiUrl"
                  className="form-control"
                  value={formData.apiUrl}
                  onChange={handleChange}
                  placeholder="https://api.domain.com"
                />
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={autoFillBackendFromApi}>
                  تعبئة تلقائية
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">رابط الواجهة الأمامية</label>
              <input type="url" name="storeFrontendUrl" className="form-control" value={formData.storeFrontendUrl} onChange={handleChange} placeholder="https://store.example.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Port</label>
              <input
                type="text"
                name="port"
                className="form-control"
                value={formData.port}
                onChange={handleChange}
                placeholder="5000"
              />
            </div>
            <div className="form-group">
              <label className="form-label">رابط ويب هوك تطبيق المراسلة (Facebook / Instagram)</label>
              <input
                type="url"
                name="messagingAppWebhookUrl"
                className="form-control"
                value={formData.messagingAppWebhookUrl}
                onChange={handleChange}
                placeholder="https://your-messaging-app.com/webhook/facebook"
              />
            </div>
          </div>
        )}

        {/* Database Tab */}
        {activeTab === 'database' && (
          <div>
            <div className="form-group">
              <label className="form-label">MongoDB URI</label>
              <input type="text" name="mongodbUri" className="form-control" value={formData.mongodbUri} onChange={handleChange} placeholder="mongodb://..." />
            </div>
            <div className="form-group">
              <label className="form-label">اسم قاعدة البيانات</label>
              <input type="text" name="mongodbDbName" className="form-control" value={formData.mongodbDbName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">JWT Secret</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="text" name="jwtSecret" className="form-control" value={formData.jwtSecret} onChange={handleChange} />
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleGenerateJwtSecret}>توليد عشوائي</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">JWT Expire</label>
              <input type="text" name="jwtExpire" className="form-control" value={formData.jwtExpire || '7d'} onChange={handleChange} placeholder="7d" />
            </div>
          </div>
        )}

        {/* R2 Tab */}
        {activeTab === 'r2' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Access Key ID</label>
                <input type="text" name="r2AccessKeyId" className="form-control" value={formData.r2AccessKeyId} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Secret Access Key</label>
                <input type="text" name="r2SecretAccessKey" className="form-control" value={formData.r2SecretAccessKey} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">اسم الـ Bucket</label>
              <input type="text" name="r2BucketName" className="form-control" value={formData.r2BucketName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Public URL</label>
              <input type="url" name="r2PublicUrl" className="form-control" value={formData.r2PublicUrl} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Endpoint</label>
              <input type="url" name="r2Endpoint" className="form-control" value={formData.r2Endpoint} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Public Base URL</label>
              <input type="url" name="r2PublicBaseUrl" className="form-control" value={formData.r2PublicBaseUrl} onChange={handleChange} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="button" className="btn btn-success btn-sm" onClick={handleCreateR2} disabled={creatingR2}>
                {creatingR2 ? 'جاري الإنشاء...' : 'إنشاء R2 Bucket و Token'}
              </button>
            </div>
          </div>
        )}

        {/* Telegram Tab */}
        {activeTab === 'telegram' && (
          <div>
            <div className="form-group">
              <label className="form-label">Bot Token</label>
              <input type="text" name="telegramBotToken" className="form-control" value={formData.telegramBotToken} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Bot Username</label>
              <input type="text" name="telegramBotUsername" className="form-control" value={formData.telegramBotUsername} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Notification Token</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="text" name="telegramNotificationToken" className="form-control" value={formData.telegramNotificationToken} onChange={handleChange} />
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleGenerateTelegramNotificationToken}>توليد عشوائي</button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Tab */}
        {activeTab === 'admin' && (
          <div>
            <div className="form-group">
              <label className="form-label">البريد الإلكتروني الافتراضي</label>
              <input type="email" name="adminDefaultEmail" className="form-control" value={formData.adminDefaultEmail} onChange={handleChange} placeholder="admin@domain.com" />
            </div>
            <div className="form-group">
              <label className="form-label">كلمة المرور الافتراضية</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="text" name="adminDefaultPassword" className="form-control" value={formData.adminDefaultPassword} onChange={handleChange} />
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleGenerateAdminPassword}>توليد عشوائي</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">الاسم الافتراضي</label>
              <input type="text" name="adminDefaultName" className="form-control" value={formData.adminDefaultName || 'مدير النظام'} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">الهاتف الافتراضي</label>
              <input type="text" name="adminDefaultPhone" className="form-control" value={formData.adminDefaultPhone} onChange={handleChange} />
            </div>
          </div>
        )}

        {/* Business Type Tab */}
        {activeTab === 'business' && (
          <div>
            <div className="form-group">
              <label className="form-label">نوع النشاط</label>
              <select name="businessType" className="form-control" value={formData.businessType} onChange={handleChange}>
                <option value="">اختر نوع النشاط</option>
                <option value="electronics">إلكترونيات</option>
                <option value="clothes">ملابس</option>
                <option value="home-accessories">إكسسوارات منزلية</option>
              </select>
            </div>
          </div>
        )}

        {/* VPS Tab */}
        {activeTab === 'vps' && (
          <div>
            <div className="form-group">
              <label className="form-label">خادم VPS</label>
              <select
                name="vps"
                className="form-control"
                value={formData.vps}
                onChange={handleChange}
              >
                <option value="">لا يوجد (اختياري)</option>
                {vpsList.map((vps) => (
                  <option key={vps._id} value={vps._id}>
                    {vps.ip}
                    {vps.provider ? ` (${vps.provider})` : ''}
                    {vps.nickname ? ` - ${vps.nickname}` : ''}
                    {typeof vps.customerCount === 'number' ? ` - العملاء: ${vps.customerCount}` : ''}
                  </option>
                ))}
              </select>
              <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                اختر خادم VPS مرتبط بهذا العميل. يمكن لعدة عملاء مشاركة نفس الخادم VPS.
              </small>
            </div>
          </div>
        )}

        {/* AI Tab */}
        {activeTab === 'ai' && (
          <div>
            <div className="form-group">
              <label className="form-label">OpenAI API Key</label>
              <input type="text" name="openaiApiKey" className="form-control" value={formData.openaiApiKey} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">OpenAI Model</label>
              <input type="text" name="openaiModel" className="form-control" value={formData.openaiModel || 'gpt-4.1-nano-2025-04-14'} onChange={handleChange} placeholder="gpt-4.1-nano-2025-04-14" />
            </div>
            <div className="form-group">
              <label className="form-label">Agent Max Iterations</label>
              <input type="number" name="aiAgentMaxIterations" className="form-control" value={formData.aiAgentMaxIterations} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">AI Image Callback URL</label>
              <input
                type="url"
                name="aiImageCallbackUrl"
                className="form-control"
                value={formData.aiImageCallbackUrl}
                onChange={handleChange}
                placeholder="https://api.example.com/api/ai-image/callback"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Qdrant Collection Name</label>
              <input type="text" name="qdrantCollectionName" className="form-control" value={formData.qdrantCollectionName} onChange={handleChange} placeholder="domain_products" />
            </div>
            <div className="form-group">
              <label className="form-label">OpenAI Project ID</label>
              <input
                type="text"
                className="form-control"
                name="openaiProjectId"
                value={formData.openaiProjectId}
                onChange={handleChange}
                placeholder="proj_xxxxxxxxxxxxxxxxxxxxxx"
              />
              <small className="form-text text-muted">
                معرف المشروع الخاص بـ OpenAI لهذا العميل (اختياري). يُستخدم لتتبع التكاليف وخصمها من رصيد OpenAI.
              </small>
            </div>
          </div>
        )}

        {/* Agent config tab (AI agent backend .env) */}
        {activeTab === 'agent' && (
          <div>
            <div className="form-group">
              <label className="form-label">منفذ الوكيل (Agent Port)</label>
              <input type="text" name="agentPort" className="form-control" value={formData.agentPort} onChange={handleChange} placeholder="4000" />
              <small className="form-text text-muted">منفذ مختلف عن منفذ المتجر</small>
            </div>
            <div className="form-group">
              <label className="form-label">رابط API الوكيل (Agent API URL)</label>
              <input type="url" name="agentApiUrl" className="form-control" value={formData.agentApiUrl} onChange={handleChange} placeholder="https://agent-api.domain.com" />
            </div>
            <div className="form-group">
              <label className="form-label">MongoDB URI (قاعدة بيانات الوكيل)</label>
              <input type="text" name="agentMongodbUri" className="form-control" value={formData.agentMongodbUri} onChange={handleChange} placeholder="mongodb://..." />
            </div>
            <div className="form-group">
              <label className="form-label">اسم قاعدة بيانات الوكيل</label>
              <input type="text" name="agentMongodbDbName" className="form-control" value={formData.agentMongodbDbName} onChange={handleChange} placeholder="agent_db" />
            </div>
            <div className="form-group">
              <label className="form-label">Webhook App Secret (X-Hub-Signature-256)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="text" name="webhookAppSecret" className="form-control" value={formData.webhookAppSecret} onChange={handleChange} placeholder="سري لمطابقة التوقيع" />
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleGenerateWebhookAppSecret}>توليد</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Webhook Replay Window (ثانية)</label>
              <input type="number" name="webhookReplayWindowSeconds" className="form-control" value={formData.webhookReplayWindowSeconds} onChange={handleChange} min={0} placeholder="300" />
            </div>
            <div className="form-group">
              <label className="form-label">رابط API المتجر (Ecommerce API URL)</label>
              <input type="url" name="ecommerceApiUrl" className="form-control" value={formData.ecommerceApiUrl} onChange={handleChange} placeholder="https://api.store.com" />
            </div>
            <div className="form-group">
              <label className="form-label">اسم المتجر (عربي)</label>
              <input type="text" name="storeNameAr" className="form-control" value={formData.storeNameAr} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">عنوان المتجر</label>
              <input type="text" name="storeAddress" className="form-control" value={formData.storeAddress} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">فيسبوك المتجر</label>
              <input type="url" name="storeFacebook" className="form-control" value={formData.storeFacebook} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">انستغرام المتجر</label>
              <input type="url" name="storeInstagram" className="form-control" value={formData.storeInstagram} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">نوع المتجر (Store Type)</label>
              <input type="text" name="storeType" className="form-control" value={formData.storeType} onChange={handleChange} placeholder="electronics, clothes, ..." />
            </div>
            <div className="form-group">
              <label className="form-label">رابط واجهة وكيل الذكاء الاصطناعي (AI Agent Frontend URL)</label>
              <input type="url" name="aiAgentFrontendUrl" className="form-control" value={formData.aiAgentFrontendUrl} onChange={handleChange} placeholder="https://agent.domain.com" />
            </div>
            <div className="form-group">
              <label className="form-label">نطاق البريد للجلسات (Session Email Domain)</label>
              <input type="text" name="sessionEmailDomain" className="form-control" value={formData.sessionEmailDomain} onChange={handleChange} placeholder="example.com" />
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <span>❌</span>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span>✅</span>
            <span>{success}</span>
          </div>
        )}

        {apiToken && (
          <div className="card" style={{ marginBottom: '1.5rem', backgroundColor: 'var(--gray-50)', border: '1px solid var(--gray-200)' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: 'var(--gray-900)', fontSize: '0.875rem' }}>رمز API:</strong>
            </div>
            <code style={{ 
              display: 'block', 
              padding: '0.875rem', 
              backgroundColor: 'white', 
              borderRadius: 'var(--radius-md)', 
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
              border: '1px solid var(--gray-200)',
              color: 'var(--gray-900)'
            }}>
              {apiToken}
            </code>
            <small style={{ color: 'var(--gray-600)', display: 'block', marginTop: '0.75rem', fontSize: '0.8125rem' }}>
              ⚠️ احفظ هذا الرمز في مكان آمن. لن يتم عرضه مرة أخرى.
            </small>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--gray-200)' }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            إلغاء
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'جاري الإنشاء...' : 'إنشاء عميل'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCustomer;
